import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import {
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  X,
  Download,
  Loader2
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Alert, AlertDescription } from "./ui/alert";

const ImportClientsModal = ({ open, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
      toast.error("Seleziona un file Excel (.xlsx)");
      return;
    }
    setFile(selectedFile);
    setResult(null);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Seleziona un file da importare");
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(API + "/clients/import-excel", formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(response.data.imported + " clienti importati con successo!");
        if (onSuccess) onSuccess();
      } else {
        toast.info("Nessun nuovo cliente importato");
      }
    } catch (error) {
      console.error("Errore importazione:", error);
      const errorMsg = error.response?.data?.detail || "Errore durante l'importazione";
      toast.error(errorMsg);
      setResult({ error: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = [
      "N.Tessera",
      "Nome",
      "Cognome",
      "Codice Fiscale",
      "Data Nascita",
      "Sesso",
      "Indirizzo",
      "CAP",
      "Comune",
      "Provincia",
      "Telefono",
      "Email",
      "Quota",
      "Note"
    ];
    
    const exampleRow = [
      "001234",
      "Mario",
      "Rossi",
      "RSSMRA80A01H501U",
      "01/01/1980",
      "M",
      "Via Roma 1",
      "00100",
      "Roma",
      "RM",
      "333 1234567",
      "mario.rossi@email.com",
      "20",
      "Cliente storico"
    ];

    const csvContent = [
      headers.join(";"),
      exampleRow.join(";")
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "template_clienti.csv";
    link.click();
    
    toast.info("Template scaricato. Convertilo in .xlsx prima dell'importazione");
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  const getDropZoneClassName = () => {
    let base = "relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ";
    if (dragActive) {
      return base + "border-blue-500 bg-blue-50";
    }
    if (file) {
      return base + "border-green-400 bg-green-50";
    }
    return base + "border-slate-300 hover:border-slate-400 hover:bg-slate-50";
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-green-600" />
            Importa Clienti da Excel
          </DialogTitle>
          <DialogDescription>
            Carica un file Excel (.xlsx) con i dati dei clienti da importare
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert className="bg-blue-50 border-blue-200">
            <AlertDescription className="flex items-center justify-between">
              <span className="text-sm text-blue-700">
                Scarica il template per vedere le colonne richieste
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                className="gap-1.5 text-blue-600 border-blue-300 hover:bg-blue-100"
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4" />
                Template
              </Button>
            </AlertDescription>
          </Alert>

          <div
            className={getDropZoneClassName()}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="file-drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
              data-testid="file-input"
            />

            {file ? (
              <div className="flex flex-col items-center gap-2">
                <FileSpreadsheet className="w-12 h-12 text-green-600" />
                <p className="font-medium text-green-700">{file.name}</p>
                <p className="text-sm text-green-600">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    setResult(null);
                  }}
                  className="text-slate-500 hover:text-red-600"
                  data-testid="remove-file-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Rimuovi
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-12 h-12 text-slate-400" />
                <p className="font-medium text-slate-600">
                  Trascina qui il file Excel
                </p>
                <p className="text-sm text-slate-500">
                  oppure clicca per selezionarlo
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  Formati supportati: .xlsx, .xls
                </p>
              </div>
            )}
          </div>

          <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
            <p className="font-medium mb-1">Colonne obbligatorie:</p>
            <p>Nome, Cognome, Codice Fiscale</p>
            <p className="mt-1 font-medium">Colonne opzionali:</p>
            <p>N.Tessera, Data Nascita, Sesso, Indirizzo, CAP, Comune, Provincia, Telefono, Email, Quota, Note</p>
          </div>

          {result && (
            <div className={result.error ? "rounded-lg p-4 bg-red-50" : "rounded-lg p-4 bg-green-50"}>
              {result.error ? (
                <div className="flex items-start gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Errore</p>
                    <p className="text-sm">{result.error}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-5 h-5" />
                    <p className="font-medium">{result.message}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-white rounded-lg p-2 text-center">
                      <p className="text-2xl font-bold text-green-600">{result.imported}</p>
                      <p className="text-slate-600">Importati</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center">
                      <p className="text-2xl font-bold text-amber-600">{result.skipped}</p>
                      <p className="text-slate-600">Saltati</p>
                    </div>
                  </div>

                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-2 text-xs text-red-600 bg-red-100 rounded p-2 max-h-32 overflow-y-auto">
                      <p className="font-medium mb-1">Errori riscontrati:</p>
                      <p>{result.errors.join(", ")}</p>
                      {result.errors_note && (
                        <p className="mt-1 text-slate-500">{result.errors_note}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} data-testid="cancel-import-btn">
            {result?.imported > 0 ? "Chiudi" : "Annulla"}
          </Button>
          {!result?.imported && (
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="gap-2 bg-green-600 hover:bg-green-700"
              data-testid="upload-import-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importazione...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Importa Clienti
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportClientsModal;
